import React, { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Text } from '@flatfile/baseline'
import { useCollection } from '../../resources/hooks/useCollection'
import { JobStatus } from '@flatfile/api-internal/api'
import { useIdentity } from '../../hooks/useIdentity'
import useResource from '../../resources/hooks/useResource'
import { spacesUrl } from '../../utils/pageUtils'
import { WistiaPlayer } from '@wistia/wistia-player-react'
import { mixpanel } from '../../support/mixpanel'

export const FirstLoginDemoLoading: React.FC<{
  jobId: string
}> = ({ jobId }) => {
  const navigate = useNavigate()
  const { identity } = useIdentity()
  const [isJobComplete, setIsJobComplete] = useState(false)
  const hasTrackedStartRef = useRef(false)
  const hasTrackedCompletionRef = useRef(false)
  const hasTrackedFailureRef = useRef(false)
  const jobStartTimeRef = useRef<number>(Date.now())

  const apps = useCollection('APPS')
  const onboardingApp = apps.find((app) => app.name === 'Onboarding')
  const job = useResource('JOBS', jobId)

  // Track demo loading started (only once)
  useEffect(() => {
    if (!hasTrackedStartRef.current && jobId) {
      mixpanel.track('Workflow Demo Loading Started', {
        jobId,
        timestamp: Date.now(),
      })
      hasTrackedStartRef.current = true
    }
  }, [jobId])

  // Track job status changes and completion/failure
  useEffect(() => {
    if (job && job.status === JobStatus.Complete && !hasTrackedCompletionRef.current) {
      const duration = Date.now() - jobStartTimeRef.current
      mixpanel.track('Workflow Demo Job Completed', {
        jobId: job.id,
        spaceId: job?.outcome?.next?.id,
        duration,
        timestamp: Date.now(),
      })
      hasTrackedCompletionRef.current = true
      setIsJobComplete(true)
    }

    if (
      job &&
      (job.status === JobStatus.Failed || job.status === JobStatus.Canceled) &&
      !hasTrackedFailureRef.current
    ) {
      const duration = Date.now() - jobStartTimeRef.current
      mixpanel.track('Workflow Demo Job Failed', {
        jobId: job.id,
        status: job.status,
        error: job.outcome?.message,
        duration,
        timestamp: Date.now(),
      })
      hasTrackedFailureRef.current = true
      navigate(`/apps/custom/${onboardingApp?.id}/list/`, { state: { deploying: false } })
    }
  }, [job, navigate, onboardingApp?.id])

  const handleGoToDemo = () => {
    if (job && job.status === JobStatus.Complete) {
      const spaceId = job?.outcome?.next?.id

      // Track go to demo clicked
      mixpanel.track('Go to Workflow Demo Clicked', {
        jobId: job.id,
        spaceId,
        timestamp: Date.now(),
      })

      navigate(`/apps/custom/${onboardingApp?.id}/list/`, { state: { deploying: false } })

      // Open the space in a new tab - onboarding will be determined by the space's onboarding data
      if (spaceId) {
        const spaceUrl = spacesUrl(spaceId, identity.token)

        // Track demo space opened
        mixpanel.track('Workflow Demo Space Opened', {
          jobId: job.id,
          spaceId,
          spaceUrl,
          timestamp: Date.now(),
        })

        window.open(spaceUrl, '_blank', 'noopener,noreferrer')
      }
    }
  }

  return (
    <div className='w-full max-w-4xl bg-white rounded-lg shadow-xl p-0 flex flex-col overflow-hidden'>
      {/* Video/Image Section - this shows the Flatfile interface with play button */}
      <div className='w-full'>
        {/* Container with fixed aspect ratio to prevent layout shift */}
        <div
          className='aspect-video w-full bg-black-opacity-1 bg-cover bg-center'
          style={{
            backgroundImage: `url('https://fast.wistia.com/embed/medias/djx1zwd5u6/swatch')`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <WistiaPlayer mediaId='djx1zwd5u6' />
        </div>
      </div>

      {/* Text and Button Area - Note the light gray-ish background (#F0F0F0) */}
      <div className='text-center p-6 bg-gray-100 w-full items-center justify-center flex flex-col gap-6'>
        <div className='w-[506px]'>
          <Text variant='title2' as='h2' className='mb-2 text-[black]' emphasized>
            You're about to experience a Flatfile project.
          </Text>
          <Text variant='title2' className='text-[black]'>
            Take a look at some of the features people from Hubspot, Amazon, Toast and thousands more use everyday.
          </Text>
        </div>
        <div>
          <Button
            onClick={isJobComplete ? handleGoToDemo : undefined}
            loading={!isJobComplete}
            disabled={!isJobComplete}
            variant='secondary'
            className='rounded-md'
            size='lg'
          >
            {isJobComplete ? 'Go to demo' : 'Building your demo'}
          </Button>
        </div>
      </div>
    </div>
  )
}
